"use client";

import { Globe, ChartLine as LineChart, Target, ArrowRight, Sparkles, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GlobalExpertiseLocalFocus() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const capabilities = [{
    title: "Market & Comp-Set Tracking",
    description: "Daily competitor rate monitoring, local demand checks, and pickup pace tracking so your property never sells cheap rooms during high-demand dates or misses bookings during slow periods.",
    metric: "Daily Rate Checks",
    badge: "Market Strategy",
    icon: Globe
  }, {
    title: "Hands-On Revenue Management",
    description: "I adjust your room rates, manage minimum-stay rules, and audit OTA extranet promotions directly for your independent hotel or boutique resort every single week.",
    metric: "Extranet Control",
    badge: "Core Service",
    icon: LineChart
  }, {
    title: "Direct Booking & Channel Mix",
    description: "Better booking engine pricing, tighter OTA commission tier audits, and rate parity checks to keep more profit in your bank account instead of paying high OTA commissions.",
    metric: "Lower OTA Commissions",
    badge: "Distribution",
    icon: Target
  }];
  return <section data-section-id="604" id="global-expertise-local-focus" className="w-full bg-background py-20 md:py-28 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <script type="application/ld+json">
        {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ContactPoint",
        "contactType": "Consulting Excellence",
        "areaServed": "India",
        "availableLanguage": "English",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "C-1202, Vaidehi Hills, Karadva Road, Dindoli",
          "addressLocality": "Surat",
          "addressRegion": "Gujarat",
          "postalCode": "394210",
          "addressCountry": "IN"
        }
      })}
      </script>

      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {/* Centered Intro: Eyebrow + H1 + Lede */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-secondary/10 border border-secondary/25 text-secondary text-xs font-mono tracking-[0.25em] uppercase mb-5">
            <Sparkles className="size-3.5 text-secondary" />
            <span>Strategic Capabilities</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold text-foreground tracking-tight leading-[1.2] mb-6">
            Get Your Hotel's Occupancy and Rates Reviewed by Sunil Singh
          </h1>

          <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed">
            Hands-on revenue management and OTA distribution consulting for independent hotel owners and general managers across India and beyond.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {capabilities.map((cap, i) => {
          const Icon = cap.icon;
          return <div key={i} data-index={i} className="group relative bg-card border border-border/80 rounded-2xl p-8 sm:p-9 transition-all duration-300 hover:-translate-y-1.5 hover:border-secondary hover:shadow-xl flex flex-col justify-between">
                <div>
                  {/* Top Bar: Icon + Badge */}
                  <div className="flex items-center justify-between gap-4 mb-7">
                    <div className="relative flex items-center justify-center size-13 rounded-xl bg-secondary/10 border border-secondary/20 text-secondary group-hover:bg-secondary group-hover:text-primary-foreground transition-all duration-300">
                      <Icon className="size-6 stroke-[1.5]" />
                      <span className="absolute -top-1 -right-1 size-2 rounded-lg bg-secondary ring-2 ring-card" />
                    </div>
                    <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-muted-foreground bg-muted/60 px-3 py-1 rounded-md">
                      {cap.badge}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mb-3 group-hover:text-secondary transition-colors">
                    {cap.title}
                  </h3>
                  <p className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                    {cap.description}
                  </p>
                </div>

                {/* Bottom Metric & Action */}
                <div className="pt-6 border-t border-border/60 flex items-center justify-between">
                  <div className="text-xs font-mono uppercase tracking-wider text-secondary font-medium">
                    {cap.metric}
                  </div>
                  <Link to="#" className="inline-flex items-center gap-1.5 text-xs font-mono tracking-wider uppercase text-foreground group-hover:text-secondary group-hover:translate-x-1 transition-all">
                    <span>Engage</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </div>
              </div>;
        })}
        </div>

        {/* Supporting CTA Strip with Photographic Asset */}
        <div className="mt-14 md:mt-16 bg-card border border-border/70 rounded-2xl overflow-hidden shadow-sm grid grid-cols-1 lg:grid-cols-12 items-stretch">
          <div className="lg:col-span-4 min-h-52 sm:min-h-60 lg:min-h-full relative overflow-hidden bg-muted">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/luxury_hotel_revenue_consulting_meeting.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/luxury_hotel_revenue_consulting_meeting.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/luxury_hotel_revenue_consulting_meeting.png-wvc-sizes" alt="Hotel revenue consultant analyzing property distribution performance and RevPAR metrics" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
          </div>

          <div className="lg:col-span-8 p-6 sm:p-8 md:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-4 text-left">
              <div className="hidden sm:flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary shrink-0">
                <Phone className="size-5" />
              </div>
              <div>
                <p className="font-serif text-base sm:text-lg font-bold text-foreground">
                  Ready to optimize your property's RevPAR and OTA mix?
                </p>
                <p className="font-default text-xs sm:text-sm text-muted-foreground mt-1">
                  Connect directly with Sunil Singh for a diagnostic revenue assessment.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
              <Button asChild className="w-full sm:w-auto bg-secondary text-primary-foreground hover:bg-secondary/90 h-11 px-6 rounded-full font-mono text-xs tracking-[0.15em] uppercase shadow-md transition-transform hover:-translate-y-0.5">
                <Link to="#">Book Consultation</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}